import React, {useContext, useState} from 'react';
import {
    Box,
    Button,
    makeStyles, Grid, TextField
} from '@material-ui/core';
import { useTranslation } from "react-i18next";
import Typography from '@material-ui/core/Typography';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Slide from '@material-ui/core/Slide';
import AppContext from 'context/AppContext';
import Alert from '@material-ui/lab/Alert';
import SecureFetchContext from "../../context/SecureFetchContext";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import clsx from "clsx";
import getApiRoute from "../../routes/apiRoutes";

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="down" ref={ref} {...props} />;
});

const useStyles = makeStyles(() => ({
    root: {},
    button: {
        marginRight: "10px"
    }
}));

const NewPickupAddressDialog = ({ className, ...rest }) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const secureFetch = useContext(SecureFetchContext);
    const { appValues, setAppValues } = useContext(AppContext);
    const defaultStateId = appValues.states[appValues.states.findIndex(i => i.code === 'SK')]?.id || '';
    const [values, setValues] = useState({ state: defaultStateId });
    const [errors, setErrors] = useState({});
    const [successMessage, setSuccessMessage] = useState('');
    const [openDialog, setOpenDialog] = useState(false);

    const handleOpenClose = () => {
        setOpenDialog(prev => {
            const next = !prev;
            if (next) {
                setValues({ state: defaultStateId });
                setErrors({});
                setSuccessMessage('');
            }
            return next;
        });
    };

    const handleChange = (event) => {
        setValues({
            ...values,
            [event.target.name]: event.target.value
        });
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        secureFetch(getApiRoute('user_pickup_address'), values, 'POST').then(() => {
            setSuccessMessage(t('messages.data_saved'));
            secureFetch(getApiRoute('user')).then(json => {
                setAppValues({...appValues, user: json});
                setOpenDialog(false);
            });
        }).catch(json => {
            setErrors(json || {});
        });
    };

    const fieldError = (key) => {
        const e = errors[key];
        if (!e) return null;
        if (typeof e === 'string') return e;
        if (Array.isArray(e)) return e.join(', ');
        if (typeof e === 'object') {
            const first = Object.values(e).find(v => typeof v === 'string');
            return first || null;
        }
        return null;
    };

    return (
        <React.Fragment>
            <Button color="secondary" variant="contained" className={classes.button} onClick={handleOpenClose}>
                {t('button.add_address')}
            </Button>
            <Dialog
                open={openDialog}
                TransitionComponent={Transition}
                keepMounted
                disableEnforceFocus
                fullWidth
                maxWidth='md'
                onClose={handleOpenClose}
                aria-labelledby="new-pickup-address-title"
            >
                <DialogTitle disableTypography={true}>
                    <Typography variant="h2">
                        {t('tooltip.new_pickup_address')}
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <form
                        autoComplete="off"
                        className={clsx(classes.root, className)}
                        onSubmit={handleSubmit}
                        {...rest}
                    >
                        <Grid container spacing={3}>
                            <Grid item md={6} xs={12}>
                                <Grid container spacing={3}>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label={t('label.name')}
                                            name="name"
                                            onChange={handleChange}
                                            value={values.name || ''}
                                            error={!!fieldError('name')}
                                            helperText={fieldError('name')}
                                            required
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label={t('label.company')}
                                            name="company"
                                            onChange={handleChange}
                                            value={values.company || ''}
                                            error={!!fieldError('company')}
                                            helperText={fieldError('company')}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label={t('label.email')}
                                            name="email"
                                            onChange={handleChange}
                                            value={values.email || ''}
                                            error={!!fieldError('email')}
                                            helperText={fieldError('email')}
                                            required
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label={t('label.phone')}
                                            name="phone"
                                            onChange={handleChange}
                                            value={values.phone || ''}
                                            error={!!fieldError('phone')}
                                            helperText={fieldError('phone')}
                                            required
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item md={6} xs={12}>
                                <Grid container spacing={3}>
                                    <Grid item lg={7} xs={8}>
                                        <TextField
                                            fullWidth
                                            label={t('label.street')}
                                            name="street"
                                            onChange={handleChange}
                                            value={values.street || ''}
                                            error={!!fieldError('street')}
                                            helperText={fieldError('street')}
                                            required
                                        />
                                    </Grid>
                                    <Grid item lg={5} xs={4}>
                                        <TextField
                                            fullWidth
                                            label={t('label.house_number')}
                                            name="house_number"
                                            onChange={handleChange}
                                            value={values.house_number || ''}
                                            error={!!fieldError('house_number')}
                                            helperText={fieldError('house_number')}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label={t('label.city')}
                                            name="city"
                                            onChange={handleChange}
                                            value={values.city || ''}
                                            error={!!fieldError('city')}
                                            helperText={fieldError('city')}
                                            required
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label={t('label.zip')}
                                            name="zip"
                                            onChange={handleChange}
                                            value={values.zip || ''}
                                            error={!!fieldError('zip')}
                                            helperText={fieldError('zip')}
                                            required
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <FormControl fullWidth>
                                            <InputLabel id="newpickupstates">{t('label.state')}</InputLabel>
                                            <Select
                                                labelId="newpickupstates"
                                                value={values.state || ''}
                                                name="state"
                                                onChange={handleChange}
                                            >
                                                {appValues.states.map((option) => (
                                                    <MenuItem key={option.id} value={option.id}>
                                                        {option.name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </form>
                    {
                        successMessage === '' ? null :
                            <Box py={2}>
                                <Alert variant="filled" severity="success">
                                    {successMessage}
                                </Alert>
                            </Box>
                    }
                    <Box display="flex" justifyContent="flex-end" py={4}>
                        <Button color="default" variant="contained" className={classes.button} onClick={handleOpenClose}>
                            {t('button.close')}
                        </Button>
                        <Button color="primary" variant="contained" onClick={handleSubmit}>
                            {t('button.save')}
                        </Button>
                    </Box>
                </DialogContent>
            </Dialog>
        </React.Fragment>
    );
};

export default NewPickupAddressDialog;
